import { getRoleLabel, USER_ROLES } from '../constants/userRoles';
import {
  getCanonicalTeamName,
  getCanonicalTeamRoute,
} from '../config/teamAliases';

export const PROFILE_LIMITS = Object.freeze({
  bio: 500,
  city: 100,
  country: 100,
  preference: 120,
});

export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (Number.isFinite(value.seconds)) return new Date(value.seconds * 1000);
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const toDateInputValue = (value) => {
  const date = toDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
};

export const hasEditorialHistory = (profile) =>
  profile?.role === USER_ROLES.AUTHOR ||
  profile?.role === USER_ROLES.ADMIN ||
  profile?.employment?.hasWorked === true ||
  (Array.isArray(profile?.employmentHistory) &&
    profile.employmentHistory.length > 0) ||
  Boolean(profile?.hiredAt || profile?.dismissedAt);

export const getEmploymentSummary = (profile) => {
  if (!hasEditorialHistory(profile)) return null;

  const active = profile?.employment?.isActive ??
    [USER_ROLES.AUTHOR, USER_ROLES.ADMIN].includes(profile?.role);
  const periods = Array.isArray(profile?.employmentHistory)
    ? profile.employmentHistory
    : [];
  const latestPeriod = periods[periods.length - 1] || {};
  const lastRole = profile?.employment?.lastRole ||
    latestPeriod.role ||
    ([USER_ROLES.AUTHOR, USER_ROLES.ADMIN].includes(profile?.role)
      ? profile.role
      : USER_ROLES.AUTHOR);

  return {
    active,
    status: active ? 'Працює в BigSport' : 'Раніше працював у BigSport',
    role: lastRole,
    roleLabel: getRoleLabel(lastRole),
    startedAt: profile?.employment?.startedAt ||
      latestPeriod.startedAt ||
      profile?.hiredAt ||
      null,
    endedAt: active
      ? null
      : profile?.employment?.endedAt ||
        latestPeriod.endedAt ||
        profile?.dismissedAt ||
        profile?.endedAt ||
        null,
  };
};

export const normalizeSportsPreferences = (preferences = {}) => ({
  favoriteSport: String(preferences.favoriteSport || '').trim(),
  favoriteTeam: getCanonicalTeamName(preferences.favoriteTeam),
  favoriteNationalTeam: String(preferences.favoriteNationalTeam || '').trim(),
  favoriteAthlete: String(preferences.favoriteAthlete || '').trim(),
  favoriteTournament: String(preferences.favoriteTournament || '').trim(),
  favoriteTeamRoute:
    getCanonicalTeamRoute(preferences.favoriteTeam) ||
    String(preferences.favoriteTeamRoute || '').trim(),
});

export const hasSportsPreferences = (preferences) =>
  Object.entries(normalizeSportsPreferences(preferences))
    .some(([key, value]) => key !== 'favoriteTeamRoute' && Boolean(value));

export const validateProfileForm = (form) => {
  const username = form.username.trim();
  if (!username) return "Ім'я користувача не може бути порожнім";
  if (username.length < 3) return "Ім'я користувача має бути мінімум 3 символи";
  if (username.length > 30) return "Ім'я користувача має бути максимум 30 символів";
  if (form.city.trim().length > PROFILE_LIMITS.city) return 'Місто — максимум 100 символів';
  if (form.country.trim().length > PROFILE_LIMITS.country) return 'Країна — максимум 100 символів';
  if (form.bio.trim().length > PROFILE_LIMITS.bio) return 'Про себе — максимум 500 символів';

  const birthDate = form.birthDate ? new Date(`${form.birthDate}T00:00:00`) : null;
  if (birthDate && birthDate > new Date()) return 'Дата народження не може бути в майбутньому';

  const tooLongPreference = Object.values(form.sportsPreferences)
    .some((value) => String(value || '').trim().length > PROFILE_LIMITS.preference);
  if (tooLongPreference) return 'Назви спортивних вподобань — максимум 120 символів';

  return '';
};

export const buildProfileUpdate = (form) => ({
  username: form.username.trim(),
  city: form.city.trim(),
  country: form.country.trim(),
  birthDate: form.birthDate || null,
  bio: form.bio.trim(),
  sportsPreferences: normalizeSportsPreferences(form.sportsPreferences),
  showLastVisit: Boolean(form.showLastVisit),
});

export const canShowLastVisit = ({
  isOwner,
  isAdmin,
  showLastVisit,
}) => isOwner || isAdmin || showLastVisit !== false;

export const validateEmploymentForm = ({ startedAt, endedAt, isActive }) => {
  if (!startedAt) return 'Дата початку роботи обов’язкова';
  if (endedAt && endedAt < startedAt) {
    return 'Дата завершення не може бути раніше дати початку';
  }
  if (endedAt && endedAt > new Date().toISOString().slice(0, 10)) {
    return 'Дата завершення не може бути в майбутньому';
  }
  if (isActive && endedAt) {
    return 'Для чинного працівника дата завершення має бути порожньою';
  }
  return '';
};
